import React, { useState, useRef, useEffect } from 'react';
import CardUtils from '../../../utils/CardUtils';
import { classifyGroup } from '../../../utils/groupRules';

const useResponsive = () => {
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 1200,
    height: typeof window !== 'undefined' ? window.innerHeight : 800,
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = windowSize.width <= 767;
  const isTablet = windowSize.width <= 991 && windowSize.width > 767;
  const isDesktop = windowSize.width > 991;

  return { windowSize, isMobile, isTablet, isDesktop };
};

const PlayerHand = ({
  player,
  selectedCardsForGroup = [],
  isAutoArranged,
  showGroupButton,
  onCardSelect,
  onMoveCard,
  onCardDragStart,
  onCardDragOver,
  onCardDrop,
  isArranging = false,
  arrangementProgress = 0,
  onAutoArrange,
  onCreateGroup,
  onResetGroupSelection,
  onDeclaration,
  onDiscard,
  wildJokerCard,
  onBreakGroup,
  onMoveCardBetweenGroups,
  onDropGame,
  playerTimers = {},
  currentPlayerId,
  allPlayers = [],
  isMultiplayer = false,
  isPlayerTurn = false,
  actionInProgress = false,
  dynamicSizes = {},
  isMobile = false,
  containerPadding = 10,
  hideActionButtons = false,
  trailingContent = null
}) => {
  const { isMobile: responsiveMobile, isTablet, isDesktop } = useResponsive();
  const effectiveIsMobile = isMobile !== undefined ? isMobile : responsiveMobile;

  const [dragOverIndex, setDragOverIndex] = useState(-1);
  const [dragOverGroup, setDragOverGroup] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isArrangeUsed, setIsArrangeUsed] = useState(false);

    const dragItem = useRef();
  const dragNode = useRef();
  
  // === TOUCH DRAG REFS ===
  const touchDragRef = useRef({
    isDragging: false,
    cardId: null,
    sourceGroupId: null,
    startX: 0,
    startY: 0,
    lastX: 0,
    lastY: 0,
        clone: null,
    originalCard: null,
    moved: false
  });

  useEffect(() => {
    console.log('🃏 [PLAYER-HAND] Received props:', {
      playerName: player?.username,
      cardsCount: player?.cards?.length,
      isPlayerTurn,
      selectedCardsForGroupCount: selectedCardsForGroup.length
    });
  }, [player, isPlayerTurn, selectedCardsForGroup]);

  const getCardDimensions = () => {
    if (dynamicSizes && dynamicSizes.cardSize) {
      return { 
        width: dynamicSizes.cardSize.width, 
        height: dynamicSizes.cardSize.height, 
        marginRight: -Math.max(20, dynamicSizes.cardSize.width * 0.25)
      };
    }
    if (effectiveIsMobile) {
      return { width: 40, height: 56, marginRight: -25 };
    } else if (isTablet) {
      return { width: 55, height: 77, marginRight: -35 };
    } else {
      return { width: 70, height: 98, marginRight: -50 };
    }
  };

  useEffect(() => {
    if (isArrangeUsed && player?.cards) {
      setIsArrangeUsed(false);
    }
  }, [player?.cards?.length, isArrangeUsed]);

  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => setErrorMessage(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  const handleCardClick = (cardId) => {
    console.log('Card clicked:', cardId);
    if (onCardSelect) onCardSelect(cardId);
  };

    // === DESKTOP DRAG & DROP HANDLERS ===
  const handleDragStart = (e, cardId, sourceGroupId) => {
    e.dataTransfer.setData('application/json', JSON.stringify({
      cardId,
      sourceGroupId
    }));
    e.dataTransfer.effectAllowed = 'move';
    setIsDragging(true);
  };

  const handleDragOver = (e, targetGroupId, targetIndex) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetGroupId, targetIndex) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    let cardId, sourceGroupId;
    const jsonData = e.dataTransfer.getData('application/json');
    if (jsonData) {
      try {
        const data = JSON.parse(jsonData);
        cardId = data.cardId;
        sourceGroupId = data.sourceGroupId;
      } catch (parseErr) {
        console.warn('Failed to parse application/json drag data:', parseErr);
      }
    }
    if (!cardId) {
      const textData = e.dataTransfer.getData('text/plain');
      if (textData) {
        cardId = textData;
        sourceGroupId = null;
      }
    }
    if (cardId && onMoveCard) {
      onMoveCard(cardId, sourceGroupId, targetGroupId, targetIndex);
    }
  };

  const handleDragEnd = (e) => {
    setIsDragging(false);
  };

  // === MOBILE TOUCH DRAG & DROP HANDLERS ===
  // HTML5 drag/drop does NOT work on touch devices, so we implement touch events

        const removeTouchClone = () => {
    if (touchDragRef.current.clone && touchDragRef.current.clone.parentNode) {
      touchDragRef.current.clone.parentNode.removeChild(touchDragRef.current.clone);
    }
    touchDragRef.current.clone = null;
    if (touchDragRef.current.originalCard) {
      touchDragRef.current.originalCard.style.opacity = '1';
      touchDragRef.current.originalCard = null;
    }
  };

  // Removed clone card visual feedback for mobile touch drag
  // as requested by user — card snapping directly without clone effect.
  const createTouchClone = (storedCardEl, storedRect, touch) => {
    // Clone creation removed per user request — no visual clone during drag.
    // The card opacity is still dimmed to indicate it's being dragged.
    storedCardEl.style.opacity = '0.4';
    touchDragRef.current.originalCard = storedCardEl;
    setIsDragging(true);
  };

    const findDropTarget = (x, y) => {
    const elementUnder = document.elementFromPoint(x, y);
    if (!elementUnder) return { targetGroupId: null, targetIndex: -1 };
    
    let target = elementUnder;
    let targetGroupId = null;
    let targetIndex = -1;
    
    while (target && target !== document.body && target !== document.documentElement) {
      // Check for group containers
      if (target.classList && target.classList.contains('card-group')) {
        targetGroupId = target.getAttribute('data-group-id');
        // Find which card position within the group the finger is over
        const groupCards = target.querySelectorAll('.group-card');
        const groupRect = target.getBoundingClientRect();
        const relativeX = x - groupRect.left;
        let accumulatedWidth = 0;
        for (let i = 0; i < groupCards.length; i++) {
          const cardRect = groupCards[i].getBoundingClientRect();
          const cardMidX = cardRect.left + cardRect.width / 2;
          if (x < cardMidX) {
            targetIndex = i;
            break;
          }
          targetIndex = i + 1; // After this card
        }
        break;
      }
      // Check for individual cards in the hand (dropping before a specific card)
      if (target.classList && target.classList.contains('fan-card')) {
        const fanCards = document.querySelectorAll('.cards-row > .fan-card');
        for (let i = 0; i < fanCards.length; i++) {
          const cardRect = fanCards[i].getBoundingClientRect();
          const cardMidX = cardRect.left + cardRect.width / 2;
          if (x < cardMidX) {
            targetIndex = i;
            break;
          }
          targetIndex = i + 1;
        }
        break;
      }
      // Dropping on the hand area (add to end)
      if (target.classList && (target.classList.contains('player-hand') || 
          target.classList.contains('cards-scroll-area') || 
          target.classList.contains('cards-row'))) {
        break;
      }
      target = target.parentElement;
    }
    
        return { targetGroupId, targetIndex };
  };

  const handleTouchStart = (e, cardId, sourceGroupId) => {
    if (e.touches.length !== 1) return;
    
    const touch = e.touches[0];
    const cardEl = e.currentTarget;
    const rect = cardEl.getBoundingClientRect();
    
    touchDragRef.current = {
      isDragging: true,
      cardId,
      sourceGroupId,
      startX: touch.clientX,
      startY: touch.clientY,
      lastX: touch.clientX,
      lastY: touch.clientY,
      clone: null,
      originalCard: null,
      moved: false,
      // Store the card element reference and its starting rect
      // so handleTouchMove can create the clone at the correct position
      // REGARDLESS of what e.currentTarget returns during move events
      cardElement: cardEl,
      cardRect: {
        left: rect.left,
        top: rect.top,
        width: rect.width,
        height: rect.height
      },
      // Offset from the finger to the card's top-left corner
      offsetX: touch.clientX - rect.left,
      offsetY: touch.clientY - rect.top
    };
  };

    const handleTouchMove = (e) => {
    if (!touchDragRef.current.isDragging) return;
    
    const touch = e.touches[0];
    const { startX, startY } = touchDragRef.current;
    const deltaX = touch.clientX - startX;
    const deltaY = touch.clientY - startY;
    
    touchDragRef.current.lastX = touch.clientX;
    touchDragRef.current.lastY = touch.clientY;
    
    // Only start dragging after moving a minimum threshold
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    if (distance < 10) {
      // Still track position but don't create clone yet
      return;
    }
    
    touchDragRef.current.moved = true;
        e.preventDefault();
    e.stopPropagation();

        if (!touchDragRef.current.clone) {
      // Use the STORED card rect from touchStart — NOT e.currentTarget,
      // because e.currentTarget may not reliably point to the card during touchmove
      const storedCardEl = touchDragRef.current.cardElement;
      
      // Clone creation removed per user request — card dims directly without visual clone.
      // The original card is dimmed to indicate drag state.
      createTouchClone(storedCardEl, touchDragRef.current.cardRect, touch);
    }
  };

  const handleTouchEnd = (e) => {
    if (!touchDragRef.current.isDragging) return;
    
    const { cardId, sourceGroupId, moved, lastX, lastY } = touchDragRef.current;
    
    // Restore original card opacity
    if (touchDragRef.current.originalCard) {
      touchDragRef.current.originalCard.style.opacity = '1';
    }
    
    // Clean up clone
    removeTouchClone();
    touchDragRef.current.isDragging = false;
    setIsDragging(false);
    
    // If the user moved the card, determine drop target
    if (moved && cardId && onMoveCard) {
      const { targetGroupId, targetIndex } = findDropTarget(lastX, lastY);
      console.log(`[TOUCH-DROP] Card ${cardId} from ${sourceGroupId || 'hand'} to ${targetGroupId || 'hand'} at index ${targetIndex}`);
      onMoveCard(cardId, sourceGroupId, targetGroupId, targetIndex);
    }
  };

  // Global touchmove handler to prevent scrolling when dragging a card
  useEffect(() => {
    const handleGlobalTouchMove = (e) => {
      if (touchDragRef.current.isDragging && touchDragRef.current.moved) {
        e.preventDefault();
      }
    };
    document.addEventListener('touchmove', handleGlobalTouchMove, { passive: false });
    return () => document.removeEventListener('touchmove', handleGlobalTouchMove);
  }, []);

  const getCardValue = (rank) => {
    const values = { '2':2,'3':3,'4':4,'5':5,'6':6,'7':7,'8':8,'9':9,'10':10,
      'J':11,'Q':12,'K':13,'A':14,'Joker':0 };
    return values[rank] || 0;
  };

  const isCardJoker = (card) => {
    if (!card) return false;
    if (card.rank === 'Joker' || card.isJoker) return true;
    if (wildJokerCard && card.rank === wildJokerCard.rank) return true;
    return false;
  };

  const cardDims = getCardDimensions();

  const renderIndividualCards = () => {
    if (!player?.cards || player.cards.length === 0) {
      return (
        <div style={{ color: '#888', fontSize: '14px', fontStyle: 'italic', padding: '10px' }}>
          No cards received yet...
        </div>
      );
    }

    const hasGroups = player.cards.some(card => card.isGroup);

    return player.cards.map((card, index) => {
      if (card.isGroupSeparator || card.isGroup) return null;

      if (!card || !card.id || !card.suit || !card.rank) {
        console.warn(`Invalid card at index ${index}:`, card);
        return null;
      }

      const isGroupSelected = selectedCardsForGroup && selectedCardsForGroup.includes(card.id);
      const isJoker = isCardJoker(card);

      return (
        <div
          key={`${card.id}-${index}`}
          className={`fan-card ${isGroupSelected ? 'group-selected' : ''}`}
          draggable={true}
          onDragStart={(e) => handleDragStart(e, card.id, null)}
                    onDragOver={(e) => handleDragOver(e, null, index)}
          onDrop={(e) => handleDrop(e, null, index)}
          onDragEnd={handleDragEnd}
          onClick={() => handleCardClick(card.id)}
          onTouchStart={(e) => handleTouchStart(e, card.id, null)}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={{
            width: `${cardDims.width}px`,
            height: `${cardDims.height}px`,
            marginRight: hasGroups ? '0px' : `${cardDims.marginRight}px`,
            marginLeft: index === 0 ? '0px' : `${cardDims.marginRight}px`,
            transform: isGroupSelected ? 'translateY(-15px)' : 'none',
            transition: 'all 0.3s ease',
                        cursor: 'pointer',
            position: 'relative',
            flexShrink: 0,
            touchAction: 'pinch-zoom',
          }}
        >
          <img 
            src={CardUtils.getCardImage ? CardUtils.getCardImage(card) : '/images/cards/back.png'}
            alt={`${card.rank} of ${card.suit}`}
            className="card-image"
            draggable={false}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              borderRadius: '5px',
              border: isJoker ? '2px solid #FFC107' : '1px solid #333'
            }}
            onError={(e) => {
              console.error('Card image failed to load:', card);
              e.target.src = '/images/cards/back.png';
            }}
          />
        </div>
      );
    }).filter(Boolean);
  };

  const renderGroupCards = () => {
    const groupCards = player?.cards?.filter(card => card.isGroup) || [];
    if (groupCards.length === 0) return null;

    const overlapAmount = effectiveIsMobile 
      ? -Math.floor(cardDims.width * 0.3) 
      : -Math.floor(cardDims.width * 0.4);

    return groupCards.map((group, groupIndex) => {
      if (!group.cards || group.cards.length === 0) return null;

      return (
        <div
          key={`group-${group.id || groupIndex}`}
                    className="card-group"
          data-group-id={group.id}
          onDragOver={(e) => handleDragOver(e, group.id, -1)}
          onDrop={(e) => handleDrop(e, group.id, -1)}
          style={{
            display: 'inline-flex',
            flexDirection: 'column',
            margin: '0 1px',
            padding: '5px',
          }}
        >
          {(() => {
            // Same rules as the server (pure / impure sequence / set); Ace is low OR high, 3+ jokers = impure sequence
            const validation = classifyGroup(group.cards || [], wildJokerCard);
            const isValid = validation.isValid;
            return (
              <div className="group-header" style={{
                fontSize: '10px',
                color: isValid ? '#4CAF50' : '#ff6b6b',
                marginBottom: '5px',
                textAlign: 'center',
                fontWeight: 'bold'
              }}>
                {isValid ? `✓ ${validation.label}` : '✗ Invalid'}
              </div>
            );
          })()}
          <div className="group-cards" style={{ 
            display: 'flex', 
            flexDirection: 'row',
            alignItems: 'center',
            gap: 0
          }}>
            {group.cards.map((card, cardIndex) => {
              const isSelected = selectedCardsForGroup.includes(card.id);

              return (
                <div
                  key={`${card.id}-${cardIndex}`}
                                    className="group-card"
                  draggable={true}
                  onDragStart={(e) => handleDragStart(e, card.id, group.id)}
                  onDragOver={(e) => handleDragOver(e, group.id, cardIndex)}
                  onDrop={(e) => handleDrop(e, group.id, cardIndex)}
                  onDragEnd={handleDragEnd}
                  onClick={() => handleCardClick(card.id)}
                  onTouchStart={(e) => handleTouchStart(e, card.id, group.id)}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                  style={{
                    width: `${cardDims.width * 0.8}px`,
                    height: `${cardDims.height * 0.8}px`,
                    position: 'relative',
                    marginRight: cardIndex === group.cards.length - 1 ? 0 : overlapAmount,
                    zIndex: cardIndex,
                                        cursor: 'pointer',
                    transform: isSelected ? 'translateY(-10px)' : 'none',
                    transition: 'all 0.3s ease',
                    touchAction: 'pinch-zoom',
                  }}
                >
                  <img 
                    src={CardUtils.getCardImage ? CardUtils.getCardImage(card) : '/images/cards/back.png'}
                    alt={`${card.rank} of ${card.suit}`}
                    draggable={false}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      borderRadius: '3px'
                    }}
                  />
                </div>
              );
            })}
          </div>
        </div>
      );
    }).filter(Boolean);
  };

  return (
    <div
      className="player-hand"
      data-tour="hand"
      onDragOver={(e) => handleDragOver(e, null, -1)}
      onDrop={(e) => handleDrop(e, null, -1)}
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        position: 'relative',
        background: 'transparent',
        padding: 0,
        margin: 0
      }}
    >
      {errorMessage && (
        <div className="error-popup" style={{
          position: 'absolute',
          top: '5px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'rgba(220, 53, 69, 0.9)',
          color: 'white',
          padding: '8px 16px',
          borderRadius: '4px',
          zIndex: 1001,
          fontSize: '12px',
          whiteSpace: 'nowrap'
        }}>
          {errorMessage}
        </div>
      )}

            <div className="cards-scroll-area" style={{
                flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflowX: 'auto',
        overflowY: 'hidden',
        whiteSpace: 'nowrap',
        padding: effectiveIsMobile ? '2px 0 1px 0' : '12px 0 5px 0',
        scrollbarWidth: 'thin',
        msOverflowStyle: 'auto',
        background: 'transparent'
      }}>
        <div className="cards-row" style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '1px'
        }}>
          {renderGroupCards()}
          {renderIndividualCards()}
          {trailingContent}
        </div>
      </div>
    </div>
  );
};

export default PlayerHand;